/**
 * Unified symbol search logic (SYMBOL_SEARCH P1).
 * Single owner of query/results state, adapter/client fan-out and the
 * futures/crypto merge. Components only render.
 */
import { useCallback, useState } from "react";
import { CRYPTO_SYMBOLS, FUTURES_SYMBOLS } from "../constants/symbolSearch";
/**
 * Facade (D-S08): single search entry — legacy client first, adapter fallback.
 * The component never branches on client vs adapter itself.
 */
export function useStockSearchAdapter(adapter, client) {
    return useCallback(async (q, exchange) => {
        if (client) {
            // Legacy path: direct VNInvestClient
            const stocks = await client.getStocks(q, exchange);
            return stocks.map((s) => ({ symbol: s.symbol, company_name: s.company_name, exchange: s.exchange }));
        }
        if (adapter) {
            // New path: StockDataAdapter
            const symbols = await adapter.searchSymbols(q);
            return symbols
                .filter((s) => !exchange || s.exchange?.toUpperCase() === exchange)
                .map((s) => ({ symbol: s.symbol, company_name: s.name ?? "", exchange: s.exchange ?? "" }));
        }
        return [];
    }, [adapter, client]);
}
export function useSymbolSearch(opts) {
    const { adapter, client, exchangeFilter, compareExisting } = opts;
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(0);
    const searchStocks = useStockSearchAdapter(adapter, client);
    const search = useCallback(async (q, exFilter = exchangeFilter) => {
        if (!q) {
            setResults([]);
            setLoading(false);
            return null;
        }
        setLoading(true);
        try {
            const exchange = exFilter !== "ALL" ? exFilter : undefined;
            const mapped = await searchStocks(q, exchange);
            const upperQ = q.toUpperCase();
            const matchingFutures = FUTURES_SYMBOLS.filter((f) => (!exchange || f.exchange === exchange) &&
                (f.symbol.includes(upperQ) || f.company_name.toUpperCase().includes(upperQ)));
            const matchingCrypto = CRYPTO_SYMBOLS.filter((c) => c.symbol.includes(upperQ) || c.company_name.toUpperCase().includes(upperQ));
            let combined = [
                ...matchingFutures,
                ...matchingCrypto.filter((c) => !matchingFutures.some((f) => f.symbol === c.symbol)),
                ...mapped.filter((m) => !matchingFutures.some((f) => f.symbol === m.symbol) && !matchingCrypto.some((c) => c.symbol === m.symbol)),
            ];
            if (compareExisting && compareExisting.length > 0) {
                combined = combined.filter((r) => !compareExisting.includes(r.symbol));
            }
            setResults(combined);
            setHighlightIndex(0);
            setLoading(false);
            return combined.length;
        }
        catch {
            setResults([]);
            setLoading(false);
            return 0;
        }
    }, [exchangeFilter, compareExisting, searchStocks]);
    return { query, setQuery, results, loading, highlightIndex, setHighlightIndex, search };
}
